/**
 * PUBLIC TABLE (Meet / Flirt) — the owner's own mockup (art/src/lobby-elements-2026-09-22/meet-lobby-mockup.png,
 * 22.9.2026), measured by Codex into layout-public.json and drawn by the lobby kit over the table's scene: Meet in the
 * kit's own cyan, Flirt the same layout with the Flirt scene, logo and tint ("Flirt samalla logiikalla Meetin kanssa").
 * The logo, the region under it, six seats (a seated player = ring + avatar + the short pill with YOU / the name; an
 * open seat = the ring alone), the gathering board (GATHERING PLAYERS · 1 of 6 seated • 5 seats open · the house's two
 * rules with their icons — or STARTING IN and the countdown once a start is promised, OD-012/OD-032), INVITE on the
 * tinted CTA plate, HOW TO PLAY and LEAVE TABLE. The house starts the match; nobody presses start. Every word is font
 * text in its role (OD-035); every plate a piece; no script measures anything (OD-036).
 */
import { art } from '../art';
import { HeadBack } from '../ui/HeadButtons';
import { LobbyLogo } from '../ui/LobbyLogo';
import { LAYOUT, SCENE, KitButton, KitPiece, KitSeat, KitSlogans, KitText, el, tx, type KitSpan, type SeatVariantName } from '../ui/LobbyKit';
import { REGION, tableRegion } from '../ui/TableHead';
import type { PublicPlayer } from '@snw/protocol';
import { useEffect, useState } from 'preact/hooks';
import { me, pub, route, sheet, showToast, reportTarget, seatOption, serverOffset, howto as howtoPopup } from '../state/store';
import { socket } from '../net/socket';
import type { Expression } from '../ui/bits';
import { sfx } from '../audio/sfx';
import { t } from '../i18n/en';

const L = LAYOUT.public;
const mmss = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export function LobbyPublic({ code, expr }: { code: string; expr: (p: PublicPlayer) => Expression }) {
  const s = pub.value!; const meP = me.value;
  const [, tick] = useState(0); useEffect(() => { const id = setInterval(() => tick((x) => x + 1), 500); return () => clearInterval(id); }, []);
  useEffect(() => { document.body.classList.add('lobby-cut'); return () => document.body.classList.remove('lobby-cut'); }, []);
  const now = Date.now() + serverOffset.value;
  const startIn = s.gather?.startAt ? Math.max(0, Math.ceil((s.gather.startAt - now) / 1000)) : null;
  const minPlayers = s.gather?.minPlayers ?? 3;
  // OD-032 (Marko 22.9.2026): the third player promises a start 30 s away, a full table 10 s (the server's HOUSE_DEFAULTS
  // in apps/realtime/src/room.ts — the words here state the same numbers).
  const HOUSE = { early: 30, full: 10 };
  // Seats are the server's, never the array's order: the old `?? s.players[i]` fallback drew the same player twice when a
  // seat number skipped a slot (Marko's capture 16.9.2026 showed YOU in two chairs). Anyone whose seat is out of the six
  // takes the first free chair instead, so nobody is dropped either.
  const capacity = 6;
  const seats: (PublicPlayer | null)[] = Array.from({ length: capacity }, () => null);
  for (const p of s.players) if (p.seat >= 0 && p.seat < capacity && !seats[p.seat]) seats[p.seat] = p;
  for (const p of s.players) if (!seats.includes(p)) { const free = seats.indexOf(null); if (free >= 0) seats[free] = p; }
  const seated = s.players.filter((p) => p.connected).length; const open = Math.max(0, capacity - seated);
  const link = `${location.origin}/r/${code}`;
  const flirt = s.settings.roomType === 'flirt'; const kind = flirt ? 'flirt' : 'meet';
  const leave = () => { sfx.tap(); socket.leave(); history.pushState(null, '', '/play'); route.value = { name: 'play', mode: kind }; };
  const invite = () => { sfx.tap(); if (navigator.share) void navigator.share({ title: 'SayNoWay', text: 'Take a seat at my table', url: link }).catch(() => undefined); else { void navigator.clipboard?.writeText(link); showToast(t.copied); } };
  const region = tableRegion(s);
  const starting = startIn !== null && startIn <= 3;
  const counts: KitSpan[] = [{ text: String(seated), className: 'table-accent' }, { text: ' of ' }, { text: String(capacity), className: 'table-accent' }, { text: ' seated • ' }, { text: String(open), className: 'table-accent' }, { text: open === 1 ? ' seat open' : ' seats open' }];
  const status = startIn !== null ? `STARTS IN ${mmss(startIn)}` : `Starts ${HOUSE.early} s after ${minPlayers} are seated`;
  return (
    <div class={`screen abs enter lobby23 lobby47 lobby-kit theme-${kind}`} data-testid="lobby">
      <img class="cut-bg" src={art(SCENE[kind])} alt="" draggable={false} />
      <LobbyLogo kind={kind} />
      <KitText t={REGION} text={region} testid="lobby-region" />
      <KitSlogans L={L} kind={kind} />
      <HeadBack testid="lobby-back" label="Back to the tables" onClick={leave} />

      <div class="roster03" data-testid="roster">
        {seats.map((p, i) => {
          const n = i + 1; const mine = !!p && !!meP && p.id === meP.id;
          const variant: SeatVariantName = !p ? 'open' : !p.connected ? 'offline' : 'occupied';
          const st = !p ? 'open' : !p.connected ? 'offline' : 'seated';
          return (
            <KitSeat key={p ? p.id : `open-${n}`} L={L} n={n} variant={variant} p={p} mine={mine} state={p ? expr(p) : 'neutral'}
              testid={p ? `seat-${n}` : `seat-${n}-open`} dataState={st} label={p ? `${p.displayName} — ${st}` : 'Open seat. Invite someone.'}
              onClick={p
                ? (meP && !mine ? () => { reportTarget.value = { id: p.id, displayName: p.displayName, avatarId: p.avatarId, roomId: s.roomId }; } : undefined)
                : () => { sheet.value = 'invite'; }} />
          );
        })}
      </div>

      {/* the gathering board (OD-012): the seat counts and the house's two rules while gathering; STARTING IN and the countdown once a start is promised */}
      <KitPiece e={el(L, 'gather.plate')} />
      {(['gather.leaf.ul', 'gather.leaf.ur', 'gather.leaf.ll', 'gather.leaf.lr'] as const).map((id) => <KitPiece key={id} e={el(L, id)} />)}
      <KitText t={tx(L, 'gather.heading')} text={startIn !== null ? 'STARTING IN' : 'GATHERING PLAYERS'} />
      <KitText t={tx(L, 'gather.body')} parts={counts} testid="lobby-counts" />
      {startIn !== null
        ? <KitText t={tx(L, 'gather.countdown')} text={mmss(startIn)} cls={starting ? 'soon' : ''} testid="lobby-house" />
        : <>
            <KitPiece e={el(L, 'gather.clock')} /><KitText t={tx(L, 'gather.rule')} text={`Starts ${HOUSE.early} s after ${minPlayers} are seated`} testid="lobby-house" />
            <KitPiece e={el(L, 'gather.people')} /><KitText t={tx(L, 'gather.full')} text={`Full table starts in ${HOUSE.full} s`} testid="lobby-house-2" />
          </>}
      <span data-testid="btn-house-status" aria-label={status} hidden />

      {/* INVITE on the CTA plate in the table's colour; dimmed while the match is about to start */}
      <KitButton plate={el(L, 'invite.button')} testid="btn-share" label="Invite someone" off={starting} onClick={invite}>
        <KitPiece e={el(L, 'invite.button')} off={starting} /><KitPiece e={el(L, 'invite.people')} />
        <KitText t={tx(L, 'invite.label')} cls={starting ? 'off' : ''} />
      </KitButton>

      <KitButton plate={el(L, 'howto.button')} testid="lobby-howto" label="How to play" onClick={() => { sfx.tap(); howtoPopup.value = { onDone: () => { howtoPopup.value = null; } }; }}>
        <KitPiece e={el(L, 'howto.button')} /><KitPiece e={el(L, 'howto.icon')} />
        <KitText t={tx(L, 'howto.label')} />
      </KitButton>
      <KitButton plate={el(L, 'leave.button')} testid="btn-leave" label="Leave table" onClick={leave}>
        <KitPiece e={el(L, 'leave.button')} off={starting} /><KitPiece e={el(L, 'leave.icon')} />
        <KitText t={tx(L, 'leave.label')} />
      </KitButton>
    </div>
  );
}
